import queryString from "query-string";
import { OrderAdvance, PaginatedResource, Traveler, TravelerAdvance } from "~/application/types";
import { api } from "~/infrastructure/api";
import { TravelerAdvanceDTO, TravelerDTO } from "~/infrastructure/api/dtos";
import { mapTravelerAdvanceDTO, mapTravelerDTO } from "~/infrastructure/api/mappers";
import { sanitizePixKey } from "~/application/utils/pix-functions";
import type {
  IFindByApproverParams,
  IOrderAdvanceService,
  PayAdvanceProps,
  UpdatePaymentReceiptProps,
} from "./IOrderAdvanceService";
import { mapExpenseStatusDTO } from "~/infrastructure/api/mappers/mapExpenseStatusDTO";
import { mapExpenseReportDTO } from "~/infrastructure/api/mappers/mapExpenseReportDTO";
import dayjs from "dayjs";
import { FormDataBuilder } from "~/application/utils/object-functions";
import { DateFormats, displayDate } from "~/utils/date.utils";

export function parseTravelerDTO(traveler: TravelerDTO): Traveler {
  return mapTravelerDTO(traveler);
}

export function parseTravelerAdvanceDTO(data: TravelerAdvanceDTO): TravelerAdvance {
  return mapTravelerAdvanceDTO(data);
}

export class OrderAdvanceService implements IOrderAdvanceService {
  async create(data: OrderAdvance): Promise<TravelerAdvance[]> {
    const url = "/travelers/advance-orders";

    const body = data.travelers.map((item) => ({
      traveler_uuid: item.traveler?.uuid,
      order_uuid: data.orderId ?? null,
      reason_expense_uuid: item.reasonExpense?.uuid,
      value: item.value,
      date_of_need: item.dateOfNeed,
      description: item.description,
      cost_center_uuid: item.costCenter?.uuid,
      project_uuid: item.project?.uuid,
      phase_uuid: item.phase?.uuid,
      card_uuid: item.cardId,
      pix_key: sanitizePixKey(item.pixKey, item.pixType),
      pix_type: item.pixType,
      bank_institution: item.bankInstitution,
    }));

    return await api
      .post<{ data: TravelerAdvanceDTO[] }>(url, body)
      .then(({ data: response }) => response.data.map(mapTravelerAdvanceDTO));
  }

  async update(data: TravelerAdvance): Promise<TravelerAdvance> {
    const url = `/travelers/advance-orders/${data.uuid}`;

    const formDataBuilder = new FormDataBuilder({
      reason_expense_uuid: data.reasonExpense?.uuid,
      value: data.value,
      date_of_need: displayDate(data.dateOfNeed, DateFormats.ISO_DATE),
      description: data.description,
      cost_center_uuid: data.costCenter?.uuid,
      project_uuid: data.project?.uuid,
      phase_uuid: data.phase?.uuid,
      branch_uuid: data.branch?.uuid,
      pix_key: sanitizePixKey(data.pixKey, data.pixType),
      pix_type: data.pixType,
      bank_institution: data.bankInstitution,
    });

    const formData = formDataBuilder.getFormData();

    return api
      .post(url, formData, { "Content-Type": "multipart/form-data" })
      .then(({ data }) => data);
  }

  async append(orderId: string, data: TravelerAdvance[]): Promise<TravelerAdvance[]> {
    const url = `/orders/${orderId}/travelers/advance-orders`;
    return await api
      .post<{ data: TravelerAdvanceDTO[] }>(
        url,
        data.map((item) => ({
          traveler_uuid: item.traveler?.uuid as string,
          description: item.description,
          value: item.value,
        })),
      )
      .then(({ data: response }) => response.data.map(mapTravelerAdvanceDTO));
  }

  async findByOrderId(orderId: string): Promise<TravelerAdvance[]> {
    const url = `/orders/${orderId}/travelers/advance-orders`;
    return await api
      .get<{ data: TravelerAdvanceDTO[] }>(url)
      .then(({ data: response }) => response.data.map(mapTravelerAdvanceDTO));
  }

  async findById(id: string): Promise<TravelerAdvance> {
    const url = `/orders/travelers/advance-orders/${id}`;
    return await api.get<TravelerAdvanceDTO>(url).then(({ data }) => mapTravelerAdvanceDTO(data));
  }

  async findByApprover({
    customerEmployeeId,
    statuses,
    approverName,
    endDate,
    issuerName,
    startDate,
    travelerName,
    orderNumber,
    page,
    withExpenseReport,
    type,
  }: IFindByApproverParams): Promise<PaginatedResource<TravelerAdvance[]>> {
    const url = queryString.stringifyUrl(
      {
        url: "/travelers/advance-orders",
        query: {
          customer_employee_uuid: customerEmployeeId,
          statuses,
          page: page,
          approver_name: approverName,
          end_date: endDate ? dayjs(endDate, "DD/MM/YYYY").format("YYYY-MM-DD") : null,
          issuer_name: issuerName,
          start_date: startDate ? dayjs(startDate, "DD/MM/YYYY").format("YYYY-MM-DD") : null,
          traveler_name: travelerName,
          order_number: orderNumber,
          with_expense_report: withExpenseReport,
          type,
        },
      },
      { arrayFormat: "bracket" },
    );

    return await api
      .get<PaginatedResource<TravelerAdvanceDTO[]>>(url)
      .then(({ data: { data: response, ...rest } }) => ({
        ...rest,
        data: response.map(mapTravelerAdvanceDTO),
      }));
  }

  async findReports({ orderId, travelerId }: { orderId?: string; travelerId?: string }) {
    return await api
      .get(`orders/${orderId}/travelers/${travelerId}/advance-orders`)
      .then((data) => mapExpenseReportDTO(data.data));
  }

  async summary() {
    return await api
      .get("/advance-orders/summary")
      .then((data) => mapExpenseStatusDTO(data.data.summary));
  }

  async pay(data: PayAdvanceProps): Promise<void> {
    const url = `/travelers/advance-orders/${data.advanceOrder.uuid}/pay`;

    const formDataBuilder = new FormDataBuilder({
      payment_method: data.paymentMethod,
      payment_receipt_image: data.paymentReceiptImage,
    });

    const formData = formDataBuilder.getFormData();

    return api
      .post(url, formData, { "Content-Type": "multipart/form-data" })
      .then(({ data }) => data);
  }

  async updatePaymentReceipt({
    advanceOrder,
    paymentReceiptImage,
  }: UpdatePaymentReceiptProps): Promise<void> {
    const url = `/travelers/advance-orders/${advanceOrder.uuid}`;

    const formDataBuilder = new FormDataBuilder({
      reason_expense_uuid: advanceOrder.reasonExpense?.uuid,
      value: advanceOrder.value,
      date_of_need: advanceOrder.dateOfNeed
        ? displayDate(advanceOrder.dateOfNeed, DateFormats.ISO_DATE)
        : undefined,
      description: advanceOrder.description,
      cost_center_uuid: advanceOrder.costCenter?.uuid,
      project_uuid: advanceOrder.project?.uuid,
      phase_uuid: advanceOrder.phase?.uuid,
      payment_receipt_image: paymentReceiptImage,
    });

    const formData = formDataBuilder.getFormData();

    return api
      .post(url, formData, { "Content-Type": "multipart/form-data" })
      .then(({ data }) => data);
  }

  async withoutFinishedExpenseReports(): Promise<TravelerAdvance[]> {
    const url = `/advance-orders/without-finished-expense-reports`;

    return await api
      .get<{ data: TravelerAdvanceDTO[] }>(url)
      .then(({ data: response }) => response.data.map(mapTravelerAdvanceDTO));
  }

  async findPendingAccountabilityTotalAmount(customerEmployeeId: string): Promise<number> {
    const url = `/customers/customer-employees/${customerEmployeeId}/advances/pending-accountability/total-amount`;

    return await api.get<{ amount: number }>(url).then(({ data }) => data.amount);
  }

  async findTotalOpenAccountabilityTotalAmount(customerEmployeeId: string): Promise<number> {
    const url = `/customer-employees/${customerEmployeeId}/advances/open-amount`;
    return await api.get<{ amount: number }>(url).then(({ data }) => data.amount);
  }
}
